import React from "react";
import { NavLink } from "react-router-dom";
import { ExternalLink, Leaf } from "lucide-react";
import { ADMIN_NAV } from "./adminNav";
import { cx } from "./_shared";
import { useAdminNotificationsContext } from "../../components/admin/notifications/AdminNotificationsContext";
import { usePermission } from "../../hooks/usePermission";
import { useSettings } from "../../contexts/SettingsContext";
import { resolveAssetDisplayUrl } from "../../lib/assetUrl";

/** Sidebar tông tối (navy) — tạo tương phản rõ với vùng nội dung sáng. */
function NavItem({ item, collapsed, onNavigate }) {
  const Icon = item.icon;
  const { unread } = useAdminNotificationsContext();
  const badge = item.badge === "unread" ? unread : 0;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cx(
          "group relative flex items-center rounded-xl text-sm font-medium transition-all",
          collapsed ? "h-10 w-10 justify-center" : "h-10 gap-3 px-3",
          isActive
            ? "bg-gradient-to-r from-blue-600 to-indigo-500 text-white shadow-[0_6px_16px_-6px_rgba(59,130,246,0.7)]"
            : "text-slate-400 hover:bg-white/[0.06] hover:text-white"
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} strokeWidth={isActive ? 2.25 : 1.9} className="shrink-0" />
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
          {badge > 0 &&
            (collapsed ? (
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-slate-900" aria-hidden />
            ) : (
              <span className={cx("rounded-full px-1.5 text-[10px] font-semibold leading-4", isActive ? "bg-white text-blue-700" : "bg-rose-500 text-white")}>
                {badge > 99 ? "99+" : badge}
              </span>
            ))}
        </>
      )}
    </NavLink>
  );
}

export default function AdminSidebar({ collapsed = false, onNavigate }) {
  const { can } = usePermission();
  const { settings } = useSettings();
  // Logo + tên lấy từ Cài đặt chung của website (giống header trang chủ)
  const siteName = (settings?.siteName || "").trim() || "Lòng Vòng Đà Lạt";
  const logoSrc = resolveAssetDisplayUrl(settings?.logo?.url, settings?.logo?.key);
  const [logoOk, setLogoOk] = React.useState(true);
  React.useEffect(() => setLogoOk(true), [logoSrc]);
  const groups = ADMIN_NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.perm || can(i.perm)) })).filter((g) => g.items.length);
  return (
    <div className="flex h-full w-full flex-col bg-[linear-gradient(180deg,#0f172a_0%,#111c36_60%,#14213f_100%)] text-slate-300">
      {/* Brand */}
      <div className={cx("flex h-14 shrink-0 items-center border-b border-white/10", collapsed ? "justify-center" : "gap-2.5 px-4")}>
        {logoSrc && logoOk ? (
          <img
            src={logoSrc}
            alt={siteName}
            title={siteName}
            onError={() => setLogoOk(false)}
            referrerPolicy="no-referrer"
            className="h-9 w-9 shrink-0 rounded-full bg-white object-cover ring-2 ring-white/15"
          />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 text-white shadow-[0_4px_12px_-4px_rgba(56,189,248,0.8)]">
            <Leaf size={17} strokeWidth={2.2} />
          </span>
        )}
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <p className="truncate text-sm font-semibold text-white">{siteName}</p>
            <p className="text-[11px] text-slate-400">Trang quản trị</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav aria-label="Điều hướng quản trị" className="flex-1 overflow-y-auto px-3 py-4 [scrollbar-color:rgba(255,255,255,0.15)_transparent]">
        <div className={cx("space-y-5", collapsed && "flex flex-col items-center")}>
          {groups.map((group, gi) => (
            <div key={group.key} className={cx(collapsed && "flex flex-col items-center gap-1")}>
              {!collapsed ? (
                <p className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group.title}</p>
              ) : gi > 0 ? (
                <span className="mb-1 h-px w-6 bg-white/10" aria-hidden />
              ) : null}
              <div className={cx(!collapsed && "space-y-1")}>
                {group.items.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>

      {/* Footer */}
      <div className={cx("shrink-0 border-t border-white/10 p-3", collapsed && "flex justify-center")}>
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? "Xem website" : undefined}
          className={cx(
            "flex items-center rounded-xl text-sm font-medium text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-white",
            collapsed ? "h-10 w-10 justify-center" : "h-10 gap-3 px-3"
          )}
        >
          <ExternalLink size={17} className="shrink-0" />
          {!collapsed && <span>Xem website</span>}
        </a>
      </div>
    </div>
  );
}
